// AuthPasswordRecoveryFlow (contract 06 §8). forget → Login?show_reset_info (same banner for
// known AND unknown emails — no enumeration); exchange the one-time #reset_handoff for a
// memory-only reset token; change → Login?change_success. v4 never AUTHORS a credential-bearing
// URL, but it must READ the legacy `code` link the backend still emits (contract §6), so
// `changePassword` takes a tagged credential rather than a bare reset token.
// The password/confirm mismatch is a client-side check owned by the composable.
import { AuthError } from '../models/auth.error'
import type { ResetCredential } from '../models/auth.types'
import { authService, type AuthService } from '../service/AuthService'

export type RecoveryBanner = 'resetInfo' | 'changeSuccess'

export interface AuthRecoveryDeps {
  service?: AuthService
}

export class AuthPasswordRecoveryFlow {
  private readonly service: AuthService

  constructor(deps: AuthRecoveryDeps = {}) {
    this.service = deps.service ?? authService
  }

  /** Always resolves to the same login banner (backend hides whether the email exists). */
  async requestResetLink(email: string): Promise<{ ok: true; banner: 'resetInfo' }> {
    try {
      await this.service.requestResetLink(email)
      return { ok: true, banner: 'resetInfo' }
    } catch (error) {
      throw AuthError.from(error)
    }
  }

  /** Exchange the one-time fragment for a memory-only reset token (composable keeps it local). */
  async exchangeResetHandoff(fragment: string): Promise<string> {
    try {
      return await this.service.exchangeResetHandoff(fragment)
    } catch (error) {
      throw AuthError.from(error) // → auth.reset.invalidCode (expired/replayed/unknown)
    }
  }

  /** `credential` is whichever form the emailed link carried: the exchanged `reset_token` (v4
   *  fragment links) or the legacy `code` the backend still emits by default. The backend accepts
   *  both on this one endpoint, so the only difference is which field we name. */
  async changePassword(
    credential: ResetCredential,
    password: string,
    confirmation: string,
  ): Promise<{ ok: true; banner: 'changeSuccess' }> {
    try {
      await this.service.changePassword({
        ...(credential.kind === 'reset_token'
          ? { reset_token: credential.value }
          : { code: credential.value }),
        password,
        password_confirmation: confirmation,
      })
      return { ok: true, banner: 'changeSuccess' }
    } catch (error) {
      throw AuthError.from(error)
    }
  }
}
